import Promise from 'bluebird';
import _ from 'lodash';
import piction, {
  retrieveAndApplyUserConfig,
  setCookies,
  addSURL,
  addToken,
  isWorkbench,
  setDBLanguage,
  getLocaleFromPictionIndex,
  resetEnvironmentParams,
  getLanguageFromPictionIndex,
  getLanguageFromLocale,
  getDataLanguageFromLocale,
} from '../utils/piction';
import * as sessionActionTypes from './sessionActionTypes';
import { CollectionItem } from '../utils/collectionUtils';
import {
  DEFAULT_COLLECTION_ID,
  LOCAL_STORAGE_CSRF_TOKEN,
  LOCAL_STORAGE_EXT_URL,
  LOCAL_STORAGE_EXTERNAL_LOGIN,
  LOCAL_STORAGE_LANG_LIST,
  LOCAL_STORAGE_LOCALE_KEY,
  LOCAL_STORAGE_SURL_KEY,
} from '../constants/session';
import appConfig from '../appConfig';
import {
  SESSION_CODE_INVALID_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
} from '../constants/sessionStateTypes';
import { addDisclaimerMessage } from './messageDialog';
import { saveState } from '../utils/localStorage';

const baseLanguageJSON = '[{"_":"ENGLISH","$":{"idx":"1","display":"English"}}]';

const setLoginData = (surl, token, surlCookie, lang, isWB, languages) => {
  saveState(LOCAL_STORAGE_CSRF_TOKEN, token);
  if (!surlCookie) {
    addSURL(surl);
    saveState(LOCAL_STORAGE_SURL_KEY, surl);
  }
  addToken(token);
  isWorkbench(isWB);
  setCookies(surlCookie);
  setDBLanguage(lang);
  saveState(LOCAL_STORAGE_LANG_LIST, appConfig.enableMultiLang ? JSON.stringify(languages) : '');
};

const parseLangList = (languages) =>
  languages.map(lang => ({ _: lang.language, $: { idx: `${lang.idx}`, display: lang.display } }));

export const validateSURLRequest = () => ({
  type: sessionActionTypes.VALIDATE_SURL_REQUEST,
});

export const validateSURLSuccess = (
  isValid,
  surl,
  token,
  surlCookie,
  username,
  isInvokeWorkbench = false,
  userCollection,
  extLogin = false,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
) => ({
  type: sessionActionTypes.VALIDATE_SURL_SUCCESS,
  isValid,
  surl,
  token,
  surlCookie,
  username,
  ...(isValid ? { isInvokeWorkbench } : {}),
  userCollection,
  extLogin,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
});

export const validateSURLFailure = (err) => ({
  type: sessionActionTypes.VALIDATE_SURL_FAILURE,
  err,
});

export const validateSURL = (surl, token, surlCookie, isRefresh = false) => function validateSURLDispatch(dispatch) {
  dispatch(validateSURLRequest());
  const externalLogin = sessionStorage.getItem(LOCAL_STORAGE_EXTERNAL_LOGIN);
  if (externalLogin) {
    sessionStorage.removeItem(LOCAL_STORAGE_EXT_URL);
    try {
      const loginData = JSON.parse(externalLogin);
      retrieveAndApplyUserConfig(loginData.SURL, loginData.CSRF, dispatch)
        .then(() => {
          const langList = loginData.languages
            ? parseLangList(loginData.languages) : JSON.parse(baseLanguageJSON);
          const locale = getLocaleFromPictionIndex(loginData.LNG_ID, langList);
          setLoginData(
            loginData.SURL,
            loginData.CSRF,
            loginData.COOKIE_ENABLED === 'T' ? loginData.COOKIE_NAME : false,
            getLanguageFromPictionIndex(loginData.LNG_ID, langList)._,
            loginData.ONLOGIN_INVOKE && loginData.ONLOGIN_INVOKE === 'WORKBENCH',
            langList,
          );
          dispatch(validateSURLSuccess(
            true,
            loginData.SURL,
            loginData.CSRF,
            loginData.COOKIE_ENABLED === 'T' ? loginData.COOKIE_NAME : false,
            loginData.USERNAME,
            loginData.ONLOGIN_INVOKE && loginData.ONLOGIN_INVOKE === 'WORKBENCH',
            loginData.AID,
            loginData.LOGIN_URL,
            loginData.PUBLIC || loginData.ACCOUNT_TYPE,
            locale,
            getDataLanguageFromLocale(locale, langList),
            loginData.PROFILE
            && loginData.PROFILE.email,
            langList,
          ));
        });
      sessionStorage.removeItem(LOCAL_STORAGE_EXTERNAL_LOGIN);
      if (loginData.LOGIN_URL) sessionStorage.setItem(LOCAL_STORAGE_EXT_URL, loginData.LOGIN_URL);
      return (loginData);
    } catch (err) {
      dispatch(sessionExpired(err, true));
      return err;
    }
  } else if (surl || surlCookie || token) {
    // set base piction param
    if (surl) addSURL(surl);
    if (token) addToken(token);
    if (!_.isNull(surlCookie)) setCookies(surlCookie);

    return piction.validate()
      .then((res) => {
        if (res.isValid && isRefresh && appConfig.useUserDefinedConfig) {
          return retrieveAndApplyUserConfig(surl, token, dispatch)
            .then(() => res);
        }
        return res;
      })
      .then((res) => {
        const langList = JSON.parse(sessionStorage.getItem(LOCAL_STORAGE_LANG_LIST) || baseLanguageJSON);
        setLoginData(
          res.surl,
          token,
          surlCookie || false,
          getLanguageFromLocale(sessionStorage.getItem(LOCAL_STORAGE_LOCALE_KEY)),
          res.res.a.on_login_invoke && res.res.a.on_login_invoke[0] === 'WORKBENCH',
          langList,
        );
        dispatch(validateSURLSuccess(
          res.isValid,
          res.surl,
          token,
          surlCookie,
          res.username,
          res.res.a.on_login_invoke && res.res.a.on_login_invoke[0] === 'WORKBENCH',
          res.res.a.a[0].$.aid,
          sessionStorage.getItem(LOCAL_STORAGE_EXT_URL),
          res.res.a.a[0].$.account_type,
          sessionStorage.getItem(LOCAL_STORAGE_LOCALE_KEY),
          getDataLanguageFromLocale(sessionStorage.getItem(LOCAL_STORAGE_LOCALE_KEY), langList),
          res.res.a.profile && res.res.a.profile[0]
          && res.res.a.profile[0].email && res.res.a.profile[0].email[0]._,
          JSON.parse(sessionStorage.getItem(LOCAL_STORAGE_LANG_LIST) || baseLanguageJSON),
        ));
        return res;
      })
      .catch((err) => {
        dispatch(sessionExpired(err, true));
        return err;
      });
  }
  return Promise.reject(new Error())
    .catch((err) => {
      dispatch(sessionExpired(err, true));
      return err;
    });
};

export const sessionExpiredFail = (err) => ({
  type: sessionActionTypes.SESSION_EXPIRED,
  err: new Error(SESSION_EXPIRED_MESSAGE),
});

export const sessionExpired = (err, isSURLFailed = false) => function sessionExpiredDispatch(dispatch) {
  const extLogin = sessionStorage.getItem(LOCAL_STORAGE_EXTERNAL_LOGIN) || appConfig.externalLogin;
  if (extLogin) {
    window.location = extLogin.toLowerCase().includes('http') ? extLogin : appConfig.cardURL + extLogin;
  } else if (isSURLFailed) dispatch(validateSURLFailure(err));
  else dispatch(sessionExpiredFail(err));
};

export const validateSessionConnectionRequest = () => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_REQUEST,
});

export const validateSessionConnectionSuccess = () => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_SUCCESS,
});

export const validateSessionConnectionFailure = (err) => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_FAILURE,
  err,
});

export const validateSessionConnection = (surl) => function validateSessionConnectionDispatch(dispatch) {
  dispatch(validateSessionConnectionRequest());
  return piction.validate(surl)
    .then((res) => {
      if (res.isValid) dispatch(validateSessionConnectionSuccess());
      else dispatch(sessionExpired(res));
      return res;
    })
    .catch((err) => {
      dispatch(sessionExpired(err));
      return err;
    });
};

/* After receiving user config- update the store with the new values */
export const updateStoreFromNewConfig = () => ({
  type: sessionActionTypes.UPDATE_STORE_FROM_APPCONFIG,
});

export const requestLoginCode = (surl, code, username) => ({
  type: sessionActionTypes.LOGIN_CODE_REQUIRED,
  surl,
  code,
  username,
});

export const loginRequest = () => ({
  type: sessionActionTypes.LOGIN_REQUEST,
});

export const loginSuccess = (
  surl,
  username,
  token,
  surlCookie,
  isInvokeWorkbench = false,
  userCollection,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
) => ({
  type: sessionActionTypes.LOGIN_SUCCESS,
  surl,
  username,
  token,
  surlCookie,
  isInvokeWorkbench,
  userCollection,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
});

export const loginFailure = (err) => ({
  type: sessionActionTypes.LOGIN_FAILURE,
  err,
});

export const login = (username, password, isPublic) => function loginDispatch(dispatch) {
  dispatch(loginRequest());
  resetEnvironmentParams();

  return piction.login(username, password)
    .then((res) => {
      if (res.isCodeNeeded && res.surl) {
        sessionStorage.setItem(LOCAL_STORAGE_SURL_KEY, res.surl);
        dispatch(requestLoginCode(res.surl, res.isCodeNeeded, username));
        return res;
      }
      if (!res.csrf || _.isEmpty(res.csrf)) {
        console.error('KAUI2: No CSRF value. review your security configuration');
        throw new Error('Problem logging in. Please Review your configuration');
      }
      if (appConfig.useUserDefinedConfig) {
        return retrieveAndApplyUserConfig(res.surl, res.csrf, dispatch)
          .then(() => res);
      }
      return res;
    })
    .then((res) => {
      if (res.csrf) {
        const langList = res.res.a.languages[0].language || JSON.parse(baseLanguageJSON);
        const locale = getLocaleFromPictionIndex(res.res.a.LNG_ID[0], langList);
        setLoginData(
          res.surl,
          res.csrf,
          res.surlCookie,
          getLanguageFromPictionIndex(
            res.res.a.LNG_ID[0],
            langList,
          )._,
          res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH',
          res.res.a.languages && res.res.a.languages[0].language,
        );
        dispatch(loginSuccess(
          res.surl,
          username,
          res.csrf,
          res.surlCookie,
          res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH',
          res.res.a.AID[0],
          res.res.a.ACCOUNT_TYPE[0],
          locale,
          getDataLanguageFromLocale(locale, langList),
          res.res.a.profile && res.res.a.profile[0]
            && res.res.a.profile[0].email && res.res.a.profile[0].email[0]._,
          res.res.a.languages && res.res.a.languages[0].language,
        ));
        if (appConfig.disclaimerMessage && appConfig.displayDisclaimer) {
          if (_.toUpper(appConfig.displayDisclaimer) === 'ALWAYS'
              || (_.toUpper(appConfig.displayDisclaimer) === 'ONCE'
                && res.res.a.LAST_LOGIN_TIME[0] === '')) {
            dispatch(addDisclaimerMessage(appConfig.disclaimerMessage));
          }
        }
      }
      return res;
    })
    .catch((err) => {
      dispatch(loginFailure(isPublic ? { message: 'Please Login Again' } : err));
      return err;
    });
};

export const codeLoginRequest = () => ({
  type: sessionActionTypes.LOGIN_CODE_REQUEST,
});

export const codeLoginSuccess = (
  surl,
  token,
  surlCookie,
  isInvokeWorkbench = false,
  userCollection,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
) => ({
  type: sessionActionTypes.LOGIN_CODE_SUCCESS,
  surl,
  token,
  surlCookie,
  isInvokeWorkbench,
  userCollection,
  acctType,
  locale,
  dataLanguage,
  email,
  languages,
});

export const codeLoginFailure = (err) => ({
  type: sessionActionTypes.LOGIN_CODE_FAILURE,
  err,
});

export const codeReLogin = () => ({
  type: sessionActionTypes.LOGIN_CODE_RELOGIN,
});

export const codeLogin = (code) => function codeLoginDispatch(dispatch) {
  dispatch(codeLoginRequest());
  const surl = sessionStorage.getItem(LOCAL_STORAGE_SURL_KEY);

  return piction.codeLogin(surl, code)
    .then((res) => {
      if (!res.csrf || _.isEmpty(res.csrf)) {
        console.error('KAUI2: No CSRF value. review your security configuration');
        throw new Error('Problem logging in. Please Review your configuration');
      }
      if (appConfig.useUserDefinedConfig) {
        return retrieveAndApplyUserConfig(res.surl, res.csrf, dispatch)
          .then(() => res);
      }
      return res;
    })
    .then((res) => {
      const langList = res.res.a.languages[0].language || JSON.parse(baseLanguageJSON);
      const locale = getLocaleFromPictionIndex(res.res.a.LNG_ID[0], langList);
      setLoginData(
        res.surl,
        res.csrf,
        res.surlCookie,
        getLanguageFromPictionIndex(
          res.res.a.LNG_ID[0],
          res.res.a.languages ? res.res.a.languages[0].language : [],
        )._,
        res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH',
        res.res.a.languages && res.res.a.languages[0].language,
      );
      dispatch(codeLoginSuccess(
        res.surl,
        res.csrf,
        res.surlCookie,
        res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH',
        res.res.a.AID[0],
        res.res.a.ACCOUNT_TYPE[0],
        locale,
        getDataLanguageFromLocale(locale, langList),
        res.res.a.profile && res.res.a.profile[0]
                && res.res.a.profile[0].email && res.res.a.profile[0].email[0]._,
        langList,
      ));
      if (appConfig.disclaimerMessage && appConfig.displayDisclaimer) {
        if (_.toUpper(appConfig.displayDisclaimer) === 'ALWAYS'
                  || (_.toUpper(appConfig.displayDisclaimer) === 'ONCE'
                    && res.res.a.LAST_LOGIN_TIME[0] === '')) {
          dispatch(addDisclaimerMessage(appConfig.disclaimerMessage));
        }
      }
      return res;
    })
    .catch((err) => {
      // TO BE RE-ADDED WHEN BACKEND SUPPORTS FAILURE ATTEMPTS
      //   dispatch(codeLoginRetry(err));
      const errCode = err.message.includes('Invalid 2FA') ? new Error(SESSION_CODE_INVALID_MESSAGE) : err;
      dispatch(codeLoginFailure(errCode));

      return err;
    });
};

export const logoutRequest = () => ({
  type: sessionActionTypes.LOGOUT_REQUEST,
});

export const logoutSuccess = () => ({
  type: sessionActionTypes.LOGOUT_SUCCESS,
});

export const logoutFailure = (err) => ({
  type: sessionActionTypes.LOGOUT_FAILURE,
  err,
});

export const logout = () => function logoutDispatch(dispatch) {
  dispatch(logoutRequest());

  const extLogin = sessionStorage.getItem(LOCAL_STORAGE_EXTERNAL_LOGIN) || appConfig.externalLogin;

  return piction.logout()
    .then((res) => {
      if (extLogin) {
        window.location = extLogin.toLowerCase().includes('http') ? extLogin : appConfig.cardURL + extLogin;
      }
      dispatch(logoutSuccess());
      return res;
    })
    .catch((err) => {
      console.log('Logout Error: ', err);
      dispatch(logoutFailure(err));
      return err;
    });
};

export const retrieveCollectionsRequest = (requestPromise) => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_REQUEST,
  requestPromise,
});

export const retrieveCollectionsSuccess = (collectionList) => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_SUCCESS,
  collectionList,
});

export const retrieveCollectionsFailure = (err) => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_FAILURE,
  err,
});

export const retrieveCollections = () => function retrieveCollectionsDispatch(dispatch) {
  const promise = piction.getCollections()
    .then((res) => {
      dispatch(retrieveCollectionsSuccess(
        res.res.a.rs
          ? res.res.a.rs.map((data) => new CollectionItem(data.$.info, data._))
          : [],
      ));
      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveCollectionsFailure(err));
      }
      return err;
    });

  dispatch(retrieveCollectionsRequest(promise));
  return promise;
};

export const retrieveAdvancedSearchCollectionsRequest = (requestPromise) => ({
  type: sessionActionTypes.RETRIEVE_ADVANCED_SEARCH_COLLECTIONS_REQUEST,
  requestPromise,
});

export const retrieveAdvancedSearchCollectionsSuccess = (advancedSearchCollectionList) => ({
  type: sessionActionTypes.RETRIEVE_ADVANCED_SEARCH_COLLECTIONS_SUCCESS,
  advancedSearchCollectionList,
});

export const retrieveAdvancedSearchCollectionsFailure = (err) => ({
  type: sessionActionTypes.RETRIEVE_ADVANCED_SEARCH_COLLECTIONS_FAILURE,
  err,
});

export const retrieveAdvancedSearchCollections = () => function retrieveAdvancedSearchCollectionsDispatch(dispatch) {
  const promise = piction.getCollections({ OPTION: 'COLLECTIONS' })
    .then((res) => {
      dispatch(retrieveAdvancedSearchCollectionsSuccess(
        res.res.a.rs
          ? res.res.a.rs.map((data) => new CollectionItem(data.$.info, data._))
          : [],
      ));
      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveAdvancedSearchCollectionsFailure(err));
      }
      return err;
    });

  dispatch(retrieveAdvancedSearchCollectionsRequest(promise));
  return promise;
};

export const retrieveMetatagsRequest = (collectionID, requestPromise) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_REQUEST,
  requestPromise,
  collectionID,
});

export const retrieveMetatagsSuccess = (collectionID, metatagList) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_SUCCESS,
  metatagList,
  collectionID,
});

export const retrieveMetatagsFailure = (collectionID, err) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_FAILURE,
  err,
  collectionID,
});

export const retrieveMetatags = (collectionID = DEFAULT_COLLECTION_ID) => function retrieveMetatagsDispatch(dispatch) {
  const promise = piction.getMetatags(
    collectionID !== DEFAULT_COLLECTION_ID ? { TAG_FOR_COLLECTION: collectionID } : { TAG_FOR_PROFILE_EDITED: '%' },
  )
    .then((res) => {
      dispatch(retrieveMetatagsSuccess(collectionID, res.res.a.META_TAG || []));
      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveMetatagsFailure(collectionID, err));
      }
      return err;
    });
  dispatch(retrieveMetatagsRequest(collectionID, promise));
  return promise;
};

export const logoutCompleted = () => ({
  type: sessionActionTypes.LOGOUT_COMPLETED,
});

export const changeLocale = (locale) => ({
  type: sessionActionTypes.CHANGE_LOCALE,
  locale,
});

export const changeDataLanguage = (selectedLanguage) => ({
  type: sessionActionTypes.CHANGE_DATA_LANGUAGE,
  selectedLanguage,
});

export const updateDefaultCollectionLabel = (label) => ({
  type: sessionActionTypes.UPDATE_DEFAULT_COLLECTION_LABEL,
  label,
});
